<template>
  <v-card class="file-item-card" :width="imageSize + 16">
    <div v-if="movable" class="d-flex mb-n3 mt-n1">
      <v-icon class="mx-auto text-medium-emphasis handle" icon="drag_handle" style="cursor: move" />
    </div>

    <v-card
      v-if="canPreviewAsImage(fileData)"
      variant="text"
      class="mx-2 mt-2 position-relative cursor-pointer"
      @click="emit('openImage', fileData._id)"
    >
      <v-img
        cover
        :width="imageSize"
        aspect-ratio="1.25"
        :src="fileSrc"
        :style="`--angle: ${fileData.angle ?? 0}deg;`"
        class="file-item-card__image"
      />

      <div v-if="primary" class="position-absolute" style="top: 4px; right: 4px; z-index: 2">
        <v-icon icon="star" color="yellow-darken-2" />
      </div>

      <div class="position-absolute bottom-0 left-0 right-0 ma-1 d-flex justify-end flex-wrap ga-1">
        <v-chip variant="elevated" color="grey" density="compact" class="text-body-small opacity-70">
          {{ $d(new Date(fileData.createdAt), "short") }}
        </v-chip>

        <!-- TODO: Add chips for showing possible tags on image (i.e. before / after photo) -->
      </div>
    </v-card>

    <v-card
      v-else
      type="flat"
      variant="tonal"
      class="mx-2 mt-2 rounded cursor-pointer"
      :width="imageSize"
      :height="imageSize / 1.25"
      :color="extensionColor(fileData.extension)"
      @click="emit('openImage', fileData._id)"
    >
      <v-card-text class="text-center d-flex justify-center align-center h-100 file-item-card__file-extension">
        <v-img v-if="getFileIconSrc(fileData)" :src="getFileIconSrc(fileData)" height="100" />
        <span v-else class="text-body-medium">{{ fileData.extension.toUpperCase() }}</span>
      </v-card-text>

      <div v-if="fileData.private" class="position-absolute" style="top: 4px; right: 4px; z-index: 2">
        <v-icon icon="lock" :color="currentTheme.dark ? 'white' : 'black'" />
      </div>
    </v-card>

    <v-list-item class="pl-2 pr-1">
      <template #title>
        <span class="text-no-wrap text-body-medium ellipsis" :title="`${fileData.description}.${fileData.extension}`">
          {{ fileData.description }}.{{ fileData.extension }}
        </span>
      </template>

      <template #append>
        <file-actions
          :id="fileData._id"
          v-model="fileData"
          :file-data="fileData"
          :hide-download="hideDownload"
          :hide-delete="hideDelete"
          :hide-rename="hideRename"
          :hide-remark="hideRemark"
          :hide-rotate="hideRotate"
          :hide-visibility="hideVisibility"
          size="x-small"
          @delete="() => emit('delete')"
        />
      </template>
    </v-list-item>
  </v-card>
</template>

<script setup lang="ts">
import isEqual from "lodash-es/isEqual"
import { useTheme } from "vuetify"

const fileData = defineModel<FileData>({ required: true })

const emit = defineEmits<{
  (e: "delete"): void
  (e: "openImage", fileId: string): void
}>()

withDefaults(
  defineProps<{
    imageSize?: number
    movable?: boolean
    hideDownload?: boolean
    hideDelete?: boolean
    hideRename?: boolean
    hideRemark?: boolean
    hideRotate?: boolean
    hideVisibility?: boolean
    primary?: boolean
  }>(),
  {
    imageSize: 200,
    movable: false,
    hideDownload: false,
    hideDelete: false,
    hideRename: false,
    hideRemark: false,
    hideRotate: false,
    hideVisibility: false,
    primary: false,
  },
)

const fileStore = useFileStore()
const { current: currentTheme } = useTheme()

const { getFileIconSrc, canPreviewAsImage } = useFileExtensions()

const fileSrc = ref("/no-image.png")

watch(
  fileData,
  async (newVal, oldVal) => {
    if (isEqual(newVal._id, oldVal?._id)) {
      return
    }

    if (canPreviewAsImage(fileData.value)) {
      fileSrc.value = await fileStore.getFileUrl(newVal._id, "/no-image.png", true)
      return
    }

    fileSrc.value = "/no-image.png"
  },
  { immediate: true, deep: true },
)
</script>

<style lang="scss">
.file-item-card {
  // dynamically updated by template style binding
  --angle: 0deg;

  .v-img__img.v-img__img--cover {
    transform: rotate(var(--angle));
  }
}

.file-item-card__image,
.file-item-card__file-extension > span {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.file-item-card__image:hover,
.file-item-card__file-extension:hover > span {
  transform: scale(1.05);
}
</style>
